import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Star, Send } from "lucide-react";
import { toast } from "sonner";

function Stars({ n, onSelect, size = 16 }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          onClick={onSelect ? () => onSelect(i) : undefined}
          disabled={!onSelect}
          className={onSelect ? "cursor-pointer" : "cursor-default"}
          aria-label={`${i} star`}
        >
          <Star size={size} className={i <= n ? "fill-gold-500 text-gold-500" : "text-ink-300"} />
        </button>
      ))}
    </div>
  );
}

export default function ProductReviews({ slug }) {
  const { user } = useAuth();
  const [data, setData] = useState({ reviews: [], average: 0, count: 0 });
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/products/${slug}/reviews`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return toast.error("Please write a short review");
    setBusy(true);
    try {
      await api.post(`/products/${slug}/reviews`, { rating, title, body });
      toast.success("Thanks for your review");
      setTitle(""); setBody(""); setRating(5);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post review");
    }
    setBusy(false);
  };

  return (
    <section className="mt-16 border-t hairline pt-10" data-testid="pdp-reviews">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Reviews</div>
          <div className="mt-2 flex items-center gap-3">
            <div className="font-display text-3xl">{data.average || "—"}</div>
            <Stars n={Math.round(data.average || 0)} size={18} />
            <div className="text-xs text-ink-500">{data.count} review{data.count === 1 ? "" : "s"}</div>
          </div>
        </div>
      </div>

      {/* Form */}
      {user ? (
        <form onSubmit={submit} className="mt-8 border hairline p-5 bg-cream-50 space-y-3">
          <div className="flex items-center gap-3">
            <span className="eyebrow">Your rating</span>
            <Stars n={rating} onSelect={setRating} size={20} />
          </div>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)"
            className="w-full h-11 bg-white border hairline px-3 text-sm" data-testid="review-title" maxLength={120} />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="How did it work for you?" rows={3}
            className="w-full bg-white border hairline p-3 text-sm" data-testid="review-body" maxLength={2000} />
          <button type="submit" disabled={busy} className="btn-primary-ink px-5 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50" data-testid="review-submit">
            <Send size={12} /> {busy ? "Posting…" : "Post review"}
          </button>
        </form>
      ) : (
        <p className="mt-8 text-sm text-ink-500">
          <a href="/auth" className="link-underline">Sign in</a> to write a review.
        </p>
      )}

      {/* List */}
      <div className="mt-8 space-y-5">
        {data.reviews.length === 0 && <div className="text-sm text-ink-500">No reviews yet. Be the first.</div>}
        {data.reviews.map((r) => (
          <div key={r.id} className="border hairline p-5 bg-white" data-testid={`review-${r.id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <Stars n={r.rating} />
                {r.title && <div className="mt-2 font-display text-lg">{r.title}</div>}
              </div>
              <div className="text-[11px] text-ink-500">
                {r.user_name} · {new Date(r.created_at).toLocaleDateString()}
              </div>
            </div>
            <p className="mt-3 text-sm text-ink-700 leading-relaxed whitespace-pre-line">{r.body}</p>
            {r.admin_reply && (
              <div className="mt-4 border-l-2 border-gold-500 pl-3 py-1 bg-cream-50">
                <div className="eyebrow text-gold-500">IMMUSKI · Reply</div>
                <p className="mt-1 text-[13px] text-ink-700 whitespace-pre-line">{r.admin_reply}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
